/**
 * Tracks "the app is working on something" for the thin bar at the top of the page
 * (same idea as angular-loading-bar in the school dashboard).
 *
 * - Every Apollo operation counts automatically (see pageLoadingLink) unless it is marked
 *   `context: { silent: true }`, which background polling should use.
 * - hold() keeps the bar running for work that is not a request, e.g. a role switch whose
 *   request has finished but whose page reload has not happened yet.
 */
const listeners = new Set();
let active = 0;
let holds = 0;

const notify = () => listeners.forEach((listener) => listener());

const counter = (increment, decrement) => {
  increment();
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    decrement();
    notify();
  };
};

export const pageLoading = {
  /** Start one unit of work; returns an idempotent function that ends it. */
  begin() {
    return counter(
      () => (active += 1),
      () => (active = Math.max(0, active - 1))
    );
  },
  /** Keep the bar running until released (or until the page unloads). */
  hold() {
    return counter(
      () => (holds += 1),
      () => (holds = Math.max(0, holds - 1))
    );
  },
  isLoading() {
    return active > 0 || holds > 0;
  },
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Test helper */
  reset() {
    active = 0;
    holds = 0;
    notify();
  },
};
